import { useEvent } from "shared/lib/useEvent"

import { useGetClarificationRequestQuery } from "entities/clarification-requests/graphql/queries.generated"
import {
  setPersistentClarificationRequestHintClosed,
  usePersistentClarificationRequestHintClosed
} from "entities/clarification-requests/model/persistent-clarification-request-hint-store"
import { useProjectTeamData } from "features/project-team/model/useProjectTeamData"

import { useCreateClarificationRequest } from "../api/useCreateClarificationRequest"
import { useDeleteClarificationRequest } from "../api/useDeleteClarificationRequest"
import { useUpdateClarificationRequest } from "../api/useUpdateClarificationRequest"
import { ClarificationRequestResourceType } from "../types/ClarificationRequest"
import { ClarificationRequestModel } from "../types/ClarificationRequestModel"

export function useClarificationRequestModel({
  resourceType,
  id,
  projectID,
  options
}: {
  resourceType: ClarificationRequestResourceType
  id: string
  projectID?: string
  options?: { skip?: boolean }
}): ClarificationRequestModel {
  const { data, loading, error } = useGetClarificationRequestQuery({
    variables: { resourceType, id },
    skip: options?.skip
  })

  const [createClarificationRequest] = useCreateClarificationRequest()
  const [updateClarificationRequest] = useUpdateClarificationRequest()
  const [deleteClarificationRequest] = useDeleteClarificationRequest()

  const currentCR = data?.clarificationRequest ?? null

  const onSubmitContent = useEvent((content: string | null | undefined) => {
    if (!content) return
    if (content === currentCR?.content) return

    if (currentCR) {
      updateClarificationRequest({ resourceType, id, content })
    } else {
      createClarificationRequest({ resourceType, id, content })
    }
  })

  const onDelete = useEvent(() => {
    deleteClarificationRequest({ resourceType, id })
  })

  const isHintClosed = usePersistentClarificationRequestHintClosed()

  const onHintClose = useEvent(() => {
    setPersistentClarificationRequestHintClosed(true)
  })

  const { team } = useProjectTeamData({ projectID: projectID ?? "" })

  const lastUpdatedByMember =
    team?.memberships.find(m => m.user?.id === currentCR?.lastUpdatedBy?.id) ??
    null

  return {
    data: {
      clarificationRequest: {
        data: currentCR ?? undefined,
        isLoading: loading,
        error: !!error
      },
      meta: {
        shouldShowHint: !isHintClosed,
        lastUpdatedByMember
      }
    },
    handlers: {
      onSubmitContent,
      onDelete,
      onHintClose
    }
  }
}
